import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Globe, 
  Code, 
  Settings, 
  Cpu,
  Monitor,
  Server,
  ArrowRight,
  Clock,
  Briefcase,
  Users,
  Award,
  MessageSquare,
  Lightbulb,
  Rocket,
  Wrench,
  ExternalLink
} from 'lucide-react';

const Home = () => {
  // Services data
  const services = [
    {
      icon: <Globe size={28} />,
      title: 'Website Development',
      description: 'Professional, responsive websites built with modern frameworks and best practices.',
      link: '/services#web-development'
    },
    {
      icon: <Monitor size={28} />,
      title: 'Web Application Development',
      description: 'Custom web applications with scalable architecture and seamless user experience.',
      link: '/services#software'
    },
    {
      icon: <Settings size={28} />,
      title: 'Technology Consulting',
      description: 'Strategic guidance on technology decisions, architecture, and digital transformation.',
      link: '/services#enterprise'
    },
    {
      icon: <Cpu size={28} />,
      title: 'IoT Integration',
      description: 'Connected solutions linking devices, sensors, and platforms for smart operations.',
      link: '/services#enterprise'
    },
    {
      icon: <Code size={28} />,
      title: 'Custom Software Development',
      description: 'Tailored software solutions designed to solve your unique business challenges.',
      link: '/services#software'
    },
    {
      icon: <Server size={28} />,
      title: 'Enterprise Engineering Solutions',
      description: 'Product lifecycle management, digital thread integration, and data systems.',
      link: '/services#enterprise'
    }
  ];

  // Technologies data
  const technologies = [
    { name: 'React', icon: '⚛️' },
    { name: 'Node.js', icon: '🟢' },
    { name: 'Cloud Platforms', icon: '☁️' },
    { name: 'REST APIs', icon: '🔗' },
    { name: 'IoT Platforms', icon: '📡' },
    { name: 'Databases', icon: '🗄️' }
  ];

  // Portfolio/Projects data
  const projects = [
    {
      title: 'E-Commerce Platform',
      description: 'Full-stack e-commerce solution with payment integration, inventory management, and analytics dashboard.',
      technologies: ['React', 'Node.js', 'MongoDB', 'Stripe']
    },
    {
      title: 'IoT Dashboard System',
      description: 'Real-time monitoring dashboard for industrial IoT sensors with data visualization and alerts.',
      technologies: ['React', 'Python', 'MQTT', 'AWS IoT']
    },
    {
      title: 'Enterprise Portal',
      description: 'Internal business portal with document management, workflow automation, and reporting.',
      technologies: ['React', 'FastAPI', 'PostgreSQL', 'Docker']
    }
  ];

  // Process steps
  const processSteps = [
    {
      number: '01',
      icon: <MessageSquare size={24} />,
      title: 'Discover',
      description: 'We understand your business, goals, and requirements thoroughly.'
    },
    {
      number: '02',
      icon: <Lightbulb size={24} />,
      title: 'Strategize Architecture',
      description: 'We design scalable architecture and create a detailed roadmap.'
    },
    {
      number: '03',
      icon: <Wrench size={24} />,
      title: 'Build, Test & Enhance',
      description: 'We develop, rigorously test, and refine until perfect.'
    },
    {
      number: '04',
      icon: <Rocket size={24} />,
      title: 'Launch & Support',
      description: 'We deploy and provide ongoing support for success.'
    }
  ];

  // Stats data
  const stats = [
    { icon: <Clock size={32} />, number: '14+', label: 'Years Experience' },
    { icon: <Briefcase size={32} />, number: '20+', label: 'Projects Delivered' },
    { icon: <Users size={32} />, number: '5+', label: 'Happy Clients' },
    { icon: <Award size={32} />, number: '98%', label: 'Client Satisfaction' }
  ];

  // WhatsApp link with custom message
  const whatsappMessage = encodeURIComponent('Hello Arvix Consulting, I would like to discuss a project.');
  const whatsappLink = `https://wa.me/919766875553?text=${whatsappMessage}`;

  return (
    <>
      {/* Hero Section */}
      <section className="hero" data-testid="hero-section">
        <div 
          className="hero-bg"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1737573296361-75315239293a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjB0ZWNobm9sb2d5JTIwdGVhbSUyMGNvbGxhYm9yYXRpb258ZW58MHx8fHwxNzcyNjQ4MjAxfDA&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="hero-overlay" />
        <div className="container">
          <div className="hero-content">
            <span className="hero-tagline">Arvix Consulting</span>
            <h1>Fast, secure and scalable solutions built with modern technologies.</h1>
            <p>
              We help startups and businesses build scalable websites, web/desktop applications 
              and digital platforms using modern technologies.
            </p>
            <div className="hero-buttons">
              <Link to="/contact" className="btn btn-white" data-testid="hero-start-project-btn">
                Start Your Project
              </Link>
              <a 
                href={whatsappLink}
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn btn-whatsapp"
                data-testid="hero-whatsapp-btn"
              >
                <i className="fab fa-whatsapp"></i> Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats/Trust Section */}
      <section className="stats-section" data-testid="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item" data-testid={`stat-${index}`}>
                <div className="stat-icon">{stat.icon}</div>
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section" data-testid="services-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Services</h2>
            <p>
              Comprehensive technology solutions to help your business grow and succeed.
            </p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <Link 
                to={service.link} 
                key={index} 
                className="service-card"
                data-testid={`service-card-${index}`}
              >
                <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <span className="service-link">
                  Learn more <ArrowRight size={16} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="section section-alt" data-testid="tech-section">
        <div className="container">
          <div className="section-header">
            <h2>Technologies We Use</h2>
            <p>
              Modern tools and frameworks to build robust, scalable solutions.
            </p>
          </div>
          <div className="tech-grid">
            {technologies.map((tech, index) => (
              <div key={index} className="tech-badge" data-testid={`tech-${index}`}>
                <span className="tech-icon">{tech.icon}</span>
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio/Projects Section */}
      <section className="section" data-testid="portfolio-section">
        <div className="container">
          <div className="section-header">
            <h2>Recent Work</h2>
            <p>
              A glimpse of projects we've delivered for our clients.
            </p>
          </div>
          <div className="projects-grid">
            {projects.map((project, index) => (
              <div key={index} className="project-card" data-testid={`project-${index}`}>
                <div className="project-content">
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>
                  <div className="project-tech">
                    {project.technologies.map((tech, techIndex) => (
                      <span key={techIndex} className="project-tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>
                <div className="project-link">
                  <ExternalLink size={18} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section section-alt" data-testid="process-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Process</h2>
            <p>
              A proven approach to delivering successful technology solutions.
            </p>
          </div>
          <div className="process-timeline">
            {processSteps.map((step, index) => (
              <div key={index} className="process-step" data-testid={`process-${index}`}>
                <div className="process-number">{step.number}</div>
                <div className="process-icon">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
                {index < processSteps.length - 1 && <div className="process-connector"></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience/Credibility Section */}
      <section className="section experience-section" data-testid="experience-section">
        <div className="container">
          <div className="experience-grid">
            <div className="experience-content">
              <h2>Why Work With Us?</h2>
              <p className="experience-intro">
                With over a decade of experience, we bring deep expertise to every project.
              </p>
              <ul className="experience-list">
                <li>
                  <span className="experience-check">✓</span>
                  <span>14+ years experience in software engineering, enterprise IoT, and web platforms</span>
                </li>
                <li>
                  <span className="experience-check">✓</span>
                  <span>Expertise in modern, scalable architectures for growing businesses</span>
                </li>
                <li>
                  <span className="experience-check">✓</span>
                  <span>End-to-end solutions from consultation to deployment and support</span>
                </li>
                <li>
                  <span className="experience-check">✓</span>
                  <span>Transparent communication and agile development process</span>
                </li>
              </ul>
              <Link to="/about" className="btn btn-primary" data-testid="experience-cta-btn">
                Learn More About Us
              </Link>
            </div>
            <div className="experience-image">
              <img 
                src="https://images.unsplash.com/photo-1565688527174-775059ac429c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHRlY2hub2xvZ3klMjBjb25zdWx0aW5nJTIwbWVldGluZyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzI2NDgyMDZ8MA&ixlib=rb-4.1.0&q=85" 
                alt="Arvix Consulting Team"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="cta-banner" data-testid="cta-section">
        <div className="container">
          <h2>Have a project in mind?</h2>
          <p>
            Let's discuss your idea and find the best technology solution for your business.
          </p>
          <div className="cta-buttons">
            <a 
              href={whatsappLink}
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn btn-whatsapp-large"
              data-testid="cta-whatsapp-btn"
            >
              <i className="fab fa-whatsapp"></i> Chat on WhatsApp
            </a>
            <Link to="/contact" className="btn btn-white" data-testid="cta-contact-btn">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
